import { useState, useEffect, useMemo } from "react";
import { Box, Tabs, Tab, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUser } from "../../pages/login/UserContextPersistent";
import ReportGenerator from "./ReportGenerator";
import { reportsApi } from "./reportsApi";
import "./reports.css";
import { useTheme } from "@mui/material/styles";
import AnalyticsChart from "./Analytics/AnalyticsChart";
import BottomSection from "../reports/BottomSection";
import ReplayVehicle from "../reports/ReplayVehicle";
import ManageDataMain from "./ManageDataMain";


const Reports = ({ school = false } = {}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { state } = useUser();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Define all possible tabs with their required features
  const allMainTabs = useMemo(() => [
    { label: "Analytics Overview", value: 0, feature: '/api/managedata/analytics' },
    { label: "Reports", value: 1, feature: '/api/managedata/reports' },
    { label: "Trip Replays", value: 2, feature: '/api/managedata/replays' },
    { label: "Data Management", value: 3, feature: '/api/managedata/datamanagement' },
  ], []);

  // Filter tabs based on user features
  const mainTabs = useMemo(() => 
    allMainTabs.filter(tab => 
      state?.features?.some(feature => feature.api_path === tab.feature)
    ), [allMainTabs, state?.features]
  );

  // Initialize with first available tab
  const [mainTab, setMainTab] = useState(mainTabs.length > 0 ? mainTabs[0].value : 0);
  const [reportTab, setReportTab] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [stops, setStops] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const reportTabs = [
    { label: "Activity Report", reportType: "activity" },
    { label: "Overspeed Report", reportType: "overspeed" },
    { label: "Inventory Report", reportType: "inventory" },
    { label: "Daily Runtime Report", reportType: "runtime" },
    { label: "Trip Report", reportType: "trip" },
    { label: "Route Deviation Report", reportType: "deviation" },
    { label: "SOS Alert Report", reportType: "sos" },
    { label: "Halt Report", reportType: "halt" },
    { label: "Stop Entry/Exit Report", reportType: "entryExit" },
  ];

  // Effect to sync tab when available tabs change
  useEffect(() => {
    if (mainTabs.length > 0 && !mainTabs.some(tab => tab.value === mainTab)) {
      setMainTab(mainTabs[0].value);
    }
  }, [mainTabs, mainTab]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const [
          routesData,
          vehiclesData,
          driversData,
          sitesData,
          stopsData,
          usersData,
        ] = await Promise.all([
          reportsApi.getRoutesByOrganization(),
          reportsApi.getVehiclesByOrganization(),
          reportsApi.getDriversByOrganization(),
          reportsApi.getSitesByOrganization(),
          reportsApi.getStopsByOrganization(),
          reportsApi.getAllUsersByOrganization(),
        ]);
        setRoutes(routesData);
        setVehicles(vehiclesData);
        setDrivers(driversData);
        setSites(sitesData);
        setStops(stopsData);
        setUsers(usersData.users || []);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const handleMainTabChange = (event, newValue) => {
    setMainTab(newValue);
  };

  const handleReportTabChange = (event, newValue) => {
    setReportTab(newValue);
  };

  const TabPanel = ({ children, value, index }) => (
    <Box role="tabpanel" hidden={value !== index} sx={{ width: "100%" }}>
      {value === index && children}
    </Box>
  );

  // Get the actual index for the current mainTab value
  const getCurrentTabIndex = () => {
    return mainTabs.findIndex(tab => tab.value === mainTab);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          m: 4,
        }}
      >
        <Tabs
          value={getCurrentTabIndex()}
          onChange={(event, newIndex) => {
            const selectedTab = mainTabs[newIndex];
            if (selectedTab) {
              setMainTab(selectedTab.value);
            }
          }}
          variant="fullWidth"
          sx={{
            color: "#414958",
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTabs-flexContainer": {
              gap: "0px",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "20px",
              fontFamily: "Roboto",
              fontWeight: 500,
              lineHeight: "100%",
              letterSpacing: "0%",
              minHeight: "56px",
              border: "1px solid #95A0B3",
              color: "#666666",
              backgroundColor: "#FFFFFF",
              "&.Mui-selected": {
                backgroundColor: "#20A8E0",
                color: "#FFFFFF",
              },
              "&:first-of-type": {
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              },
              "&:last-of-type": {
                borderTopRightRadius: "8px",
                borderBottomRightRadius: "8px",
              },
              borderRight: "none",
              "&:last-child": {
                borderRight: "1px solid #95A0B3",
              },
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          {mainTabs.map((tab) => (
            <Tab key={tab.value} label={t(tab.label)} />
          ))}
        </Tabs>
      </Box>

      {/* Analytics Tab */}
      {state?.features?.some(feature => feature.api_path === '/api/managedata/analytics') && (
        <TabPanel value={getCurrentTabIndex()} index={mainTabs.findIndex(tab => tab.feature === '/api/managedata/analytics')}>
          <Box sx={{ p: 2, background: "#FFFFFF", borderRadius: "10px" }}>
            <AnalyticsChart
              vehiclesData={vehicles}
              routesData={routes}
              driversData={drivers}
              stopsData={stops}
              sitesData={sites}
              usersData={users}
              overview={true}
              school={school}
            />
          </Box>
        </TabPanel>
      )}

      {/* Reports Tab */}
      {state?.features?.some(feature => feature.api_path === '/api/managedata/reports') && (
        <TabPanel value={getCurrentTabIndex()} index={mainTabs.findIndex(tab => tab.feature === '/api/managedata/reports')}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              background: "#FFFFFF",
              borderRadius: "10px",
              p: 2,
            }}
          >
            <Tabs
              orientation={isSmallScreen ? "horizontal" : "vertical"}
              value={reportTab}
              onChange={handleReportTabChange}
              variant={isSmallScreen ? "scrollable" : "standard"}
              scrollButtons={isSmallScreen ? "auto" : false}
              allowScrollButtonsMobile={true}
              sx={{
                borderRight: 1,
                borderColor: "divider",
                marginBottom: isSmallScreen ? "20px" : "0px",
                minWidth: "200px",
                ...(isSmallScreen && {
                  "& .MuiTabs-scroller": {
                    overflow: "auto !important",
                  },
                }),
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: { xs: "14px", sm: "16px" },
                  alignItems: "flex-start",
                  textAlign: "left",
                  padding: "12px 16px",
                  color: "#647490",
                  border: "1px solid #e0e0e0",

                  ...(isSmallScreen && {
                    minWidth: "120px",
                    flexShrink: 0,
                  }),
                  "&.Mui-selected": {
                    backgroundColor: "#20A8E0",
                    color: "#fff",
                    borderColor: "#20A8E0",
                  },
                },
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTabs-scrollButtons": {
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
              }}
            >
              {reportTabs.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>
            <Box sx={{ flex: 1, pl: 2 }}>
              {reportTabs.map((tab, index) => (
                <TabPanel key={index} value={reportTab} index={index}>
                  <ReportGenerator
                    reportType={tab.reportType}
                    vehicles={vehicles}
                    routes={routes}
                    drivers={drivers}
                    stops={stops}
                    sites={sites}
                    users={users}
                    setSites={setSites}
                  />
                </TabPanel>
              ))}
            </Box>
          </Box>
        </TabPanel>
      )}

      {/* Trip Replay Tab */}
      {state?.features?.some(feature => feature.api_path === '/api/managedata/replays') && (
        <TabPanel value={getCurrentTabIndex()} index={mainTabs.findIndex(tab => tab.feature === '/api/managedata/replays')}>
          <Box sx={{ p: 2, background: "#FFFFFF", borderRadius: "10px" }}>
            <ReplayVehicle />
          </Box>
        </TabPanel>
      )}

      {/* Data Management Tab */}
      {state?.features?.some(feature => feature.api_path === '/api/managedata/datamanagement') && (
        <TabPanel value={getCurrentTabIndex()} index={mainTabs.findIndex(tab => tab.feature === '/api/managedata/datamanagement')}>
          <Box sx={{ p: 2, background: "#FFFFFF", borderRadius: "10px" }}>
            <ManageDataMain school={school} />
          </Box>
        </TabPanel>
      )}
    </Box>
  );
};

export default Reports;
